import { Icon } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';
import { cva } from 'class-variance-authority';
import * as React from 'react';

/**
 * Figma 라이브러리 Input (443:3735) — Style × State × Size
 *
 * variant (Style)
 * - outline: 아래 테두리만 1.5px (radius 없음)
 * - filled: 흰 배경 + 1.5px 테두리 (radius large 20 · medium 16 · small 12)
 * - filledGray: 회색(grey-50) 배경, 테두리 없음 — GNB 검색 · 목록 검색
 * size: large 56 · medium 48 · small 36
 * status: default · error · success · info · warning — 테두리 · 도움말 색 (filled는 배경도 연하게)
 * State hover · focus · filled · disabled는 CSS 상태(:hover · :focus-within · 값 유무 · disabled)로 그린다.
 */
type InputVariant = 'outline' | 'filled' | 'filledGray';
type InputSize = 'large' | 'medium' | 'small';
export type InputStatus = 'default' | 'error' | 'success' | 'info' | 'warning';

const boxVariants = cva(
  'flex w-full items-center gap-3 transition-colors has-[input:disabled]:pointer-events-none',
  {
    variants: {
      variant: {
        outline:
          'border-b-[1.5px] border-grey-200 bg-transparent hover:border-grey-300 focus-within:border-grey-900',
        filled:
          'border-[1.5px] border-grey-200 bg-surface-white hover:border-grey-300 hover:bg-grey-50 focus-within:border-grey-900 focus-within:bg-surface-white has-[input:disabled]:bg-grey-100',
        filledGray: 'border-[1.5px] border-transparent bg-grey-50',
      },
      size: {
        large: 'h-14 px-3 py-4',
        medium: 'h-12 p-3',
        small: 'h-9 px-3 py-2',
      },
      status: {
        default: '',
        error:
          'border-red-500 hover:border-red-500 focus-within:border-red-500',
        success:
          'border-green-500 hover:border-green-500 focus-within:border-green-500',
        info: 'border-blue-500 hover:border-blue-500 focus-within:border-blue-500',
        warning:
          'border-yellow-500 hover:border-yellow-500 focus-within:border-yellow-500',
      },
    },
    compoundVariants: [
      { variant: 'filled', size: 'large', className: 'rounded-20' },
      { variant: 'filled', size: 'medium', className: 'rounded-16' },
      { variant: 'filled', size: 'small', className: 'rounded-12' },
      { variant: 'filledGray', size: 'large', className: 'rounded-20' },
      { variant: 'filledGray', size: 'medium', className: 'rounded-16' },
      { variant: 'filledGray', size: 'small', className: 'rounded-12' },
      // filled는 상태색 배경도 함께 바뀐다
      {
        variant: 'filled',
        status: 'error',
        className: 'bg-red-50 hover:bg-red-50 focus-within:bg-red-50',
      },
      {
        variant: 'filled',
        status: 'success',
        className: 'bg-green-50 hover:bg-green-50 focus-within:bg-green-50',
      },
      {
        variant: 'filled',
        status: 'info',
        className: 'bg-[#e3f3ff] hover:bg-[#e3f3ff] focus-within:bg-[#e3f3ff]',
      },
      {
        variant: 'filled',
        status: 'warning',
        className: 'bg-yellow-50 hover:bg-yellow-50 focus-within:bg-yellow-50',
      },
    ],
    defaultVariants: { variant: 'filled', size: 'medium', status: 'default' },
  },
);

/** 입력 글자: 값은 Medium(진한 검정), placeholder는 Regular(연회색) — filledGray medium만 placeholder도 Medium */
const fieldText: Record<InputVariant, Record<InputSize, string>> = {
  outline: {
    large: 'text-body-16-m placeholder:font-normal',
    medium: 'text-body-14-m placeholder:font-normal',
    small: 'text-body-13-r',
  },
  filled: {
    large: 'text-body-16-m placeholder:font-normal',
    medium: 'text-body-14-m placeholder:font-normal',
    small: 'text-body-13-r',
  },
  filledGray: {
    large: 'text-body-16-r',
    medium: 'text-body-14-m',
    small: 'text-body-13-r',
  },
};

const labelText: Record<InputVariant, Record<InputSize, string>> = {
  outline: {
    large: 'text-body-15-m text-fg-tertiary',
    medium: 'text-body-15-m text-fg-tertiary',
    small: 'text-body-14-r text-fg-tertiary',
  },
  filled: {
    large: 'text-body-15-m text-fg-secondary',
    medium: 'text-body-15-m text-fg-secondary',
    small: 'text-body-14-r text-fg-secondary',
  },
  filledGray: {
    large: 'text-body-15-m text-fg-secondary',
    medium: 'text-body-15-m text-fg-secondary',
    small: 'text-body-14-m text-fg-secondary',
  },
};

const helperColor: Record<InputStatus, string> = {
  default: 'text-fg-tertiary',
  error: 'text-fg-error',
  success: 'text-fg-success',
  info: 'text-fg-info',
  warning: 'text-fg-warning',
};

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  variant?: InputVariant;
  size?: InputSize;
  status?: InputStatus;
  /** 위 라벨 (Label) */
  label?: React.ReactNode;
  /** 아래 도움말 · 오류 문구 (Helper Text) */
  helperText?: React.ReactNode;
  /** 입력칸 앞 아이콘 — large · medium 24, small 16 */
  leftIcon?: React.ReactNode;
  /** 입력칸 뒤 요소 (아이콘 · 지우기 버튼 등) */
  rightSlot?: React.ReactNode;
  /** 바깥 래퍼(라벨 · 입력 · 도움말 묶음) className */
  className?: string;
  /** 입력 상자 className */
  boxClassName?: string;
  /** <input> className */
  inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'filled',
      size = 'medium',
      status = 'default',
      label,
      helperText,
      leftIcon,
      rightSlot,
      className,
      boxClassName,
      inputClassName,
      disabled,
      id,
      ...props
    },
    ref,
  ) => {
    const autoId = React.useId();
    const inputId = id ?? autoId;
    return (
      <div className={cn('flex w-full flex-col gap-1', className)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              labelText[variant][size],
              disabled && 'text-fg-disabled',
            )}
          >
            {label}
          </label>
        )}
        <div
          className={cn(boxVariants({ variant, size, status }), boxClassName)}
        >
          {leftIcon && (
            <span
              className={cn(
                'flex shrink-0 items-center',
                disabled ? 'text-fg-disabled' : 'text-fg-light',
              )}
            >
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={status === 'error' || undefined}
            className={cn(
              'w-full min-w-0 bg-transparent text-fg-primary outline-none placeholder:text-fg-light disabled:text-fg-disabled disabled:placeholder:text-fg-disabled [&::-webkit-search-cancel-button]:hidden',
              fieldText[variant][size],
              inputClassName,
            )}
            {...props}
          />
          {rightSlot && (
            <span className="flex shrink-0 items-center text-fg-primary">
              {rightSlot}
            </span>
          )}
        </div>
        {helperText && (
          <p
            className={cn(
              size === 'small' ? 'text-body-13-r' : 'text-body-14-r',
              disabled ? 'text-fg-disabled' : helperColor[status],
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

/**
 * 검색 입력 — Input filledGray + 돋보기 아이콘 (Figma GNB 검색 · 거래처 목록 검색).
 * icon을 넘기지 않으면 search-line 아이콘(크기에 맞게)을 쓴다.
 */
const SearchInput = React.forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'leftIcon' | 'variant'> & { icon?: React.ReactNode }
>(({ icon, size = 'medium', ...props }, ref) => (
  <Input
    ref={ref}
    type="search"
    variant="filledGray"
    size={size}
    leftIcon={
      icon ?? (
        <Icon
          name="search-line-24"
          className={size === 'small' ? '[&>svg]:size-4' : undefined}
        />
      )
    }
    {...props}
  />
));
SearchInput.displayName = 'SearchInput';

export { Input, SearchInput };
